import { Box } from '@mentimeter/ragnar-ui/box';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { useMemo } from 'react';
import type { RankingResult } from '@core-api/presentation/types/response';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { ScreenReaderTable } from './ScreenReaderTable';
import { RankingVisualization } from './RankingVisualization';
import type { RankingChoice } from './types';

interface RankingPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  shouldAnimate: boolean;
  choices: RankingChoice[];
  shouldHideResults: boolean;
  results: RankingResult[];
}

export function RankingPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  CanvasVisualizationArea,
  shouldAnimate,
  choices,
  shouldHideResults,
  results,
}: RankingPresentationProps) {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  const labeledResults = useMemo(() => {
    return choices.map((choice) => {
      if (shouldHideResults || !results) {
        return {
          id: choice.id,
          label: choice.label,
          score: 0,
        };
      }

      const result = results.find(
        (result) => result.interactiveContentChoiceId === choice.id,
      );

      return {
        id: choice.id,
        label: choice.label,
        score: result?.value ?? 0,
      };
    });
  }, [choices, shouldHideResults, results]);

  const areResultsEmpty = labeledResults.every((result) => result.score === 0);

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="ranking-container"
      alignItems="center"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />

      <ConditionalWrapper flex="1" width="100%" mt={3}>
        <RankingVisualization
          shouldAnimate={shouldAnimate}
          shouldHideResults={shouldHideResults || areResultsEmpty}
          labeledResults={labeledResults}
        />
        <ScreenReaderOnly>
          <ScreenReaderTable
            labeledResults={labeledResults}
            shouldHideResults={shouldHideResults}
          />
        </ScreenReaderOnly>
      </ConditionalWrapper>
    </Box>
  );
}
